import { apiFetch } from './api.js';
import fields from './fields';
import { normalizeRecords, passesFilter } from './UtilityFunctions';

/**
 * Fetching every row of a paginated sheet for export.
 *
 * This is deliberately a plain fetch loop rather than react-query: these pages
 * are a one-shot read for a download, not sheet state, and writing them into the
 * cache would evict the page the user is actually looking at.
 *
 * It calls the SAME endpoint the sheet already calls, with the same body. That
 * is the whole security story — cost redaction (redactForRequest) and every
 * permission gate apply exactly as they do for a normal page load, because the
 * request is indistinguishable from one.
 */

//Only these sheets paginate. Inventory, Committed and Credit are served by
//routes that return every row for the dealership already (routes/inventory.js,
//routes/sales.js), so "export all" is meaningless there — they always have all.
export const SHEET_ENDPOINTS = {
    Cash: '/getcash',
    CreditCleared: '/getcreditcleared',
    Cancelled: '/getcancelled',
    Customers: '/getcustomers',
    Brokers: '/getbrokers',
    Sellers: '/getsellers',
};

//node/helpers/paginate.js rejects a limit above 500, so that is the largest
//page we may ask for. Not a preference — a server-enforced ceiling.
export const PAGE_SIZE = 500;

//Refuse rather than start a download that will hang the tab. Chosen against the
//product of rows x columns: these sheets carry ~80 columns, so 5,000 rows is
//already a multi-megabyte JSON response and a workbook the browser has to hold
//in memory twice while exceljs serialises it.
export const MAX_EXPORT_ROWS = 5000;

export const canExportAll = (sheetName) => !!SHEET_ENDPOINTS[sheetName];

/**
 * Walk every page of a sheet, newest request shape identical to useSheetData's.
 *
 * onProgress({ fetched, total }) fires after each page so the dialog can show
 * movement; signal aborts mid-walk and leaves nothing behind.
 */
export async function fetchAllRows({
    sheetName, DealershipID, UserID, pagination, transformRecord = null,
    onProgress = null, signal = null,
}) {
    const path = SHEET_ENDPOINTS[sheetName];
    if (!path) throw new Error(`${sheetName} does not paginate`);

    const totalPages = Math.max(1, Math.ceil((pagination?.totalRecords || 0) / PAGE_SIZE));
    const all = [];

    for (let page = 1; page <= totalPages; page++) {
        if (signal?.aborted) throw new DOMException('Export cancelled', 'AbortError');

        //Carry the sheet's active search and date filters through unchanged, so
        //"all records" means all records THE USER IS LOOKING AT, not all rows in
        //the table. Only the paging fields are overridden.
        const body = {
            DealershipID,
            UserID,
            pagination: {
                ...pagination,
                currentPage: page,
                recordsPerPage: PAGE_SIZE,
            },
        };

        const res = await apiFetch(path, { body, signal });
        if (res.status === 401) throw new Error('Your session expired. Please log in again.');
        const payload = await res.json();
        if (res.status !== 200) throw new Error(payload.message || `HTTP ${res.status}`);

        let rows = normalizeRecords(payload.data || [], fields);
        if (transformRecord) rows = rows.map(transformRecord);
        all.push(...rows);

        onProgress?.({ fetched: all.length, total: pagination?.totalRecords ?? all.length });

        //The server is the authority on when to stop: a short page means there
        //is no more, whatever the count said. Guards against the total moving
        //under us while the walk is in flight.
        if (rows.length < PAGE_SIZE) break;
    }

    return all;
}

/**
 * Re-apply the sheet's client-side filter chips to a freshly fetched set.
 *
 * useTabularDataManager filters the loaded page in the browser, so without this
 * "export all" would silently ignore the chips the user can see above the
 * table. Same predicate, same field types — see useTabularDataManager.js.
 */
export function applyClientFilters(rows, filterObject) {
    if (!filterObject || !Object.keys(filterObject).length) return rows;
    return rows.filter((row) =>
        Object.keys(row).every((key) => {
            if (!filterObject[key]) return true;
            return passesFilter(row[key], filterObject[key], fields[key]?.dataType);
        })
    );
}
